import MuiFormControlLabel, { FormControlLabelProps as MuiFormControlLabelProps } from "@mui/material/FormControlLabel";
import { CSSProperties } from "@mui/material/styles/createTypography";
import React, { FunctionComponent, ReactElement, ReactNode, useCallback, useState } from "react";
import { useLocale } from "designSystem/locale";
import { NO_WRAP } from "designSystem/styles/style-functions";
import Tooltip from "../tooltip/Tooltip";

export interface FormControlLabelProps extends Omit<MuiFormControlLabelProps, "label"> {
	/** indicates if label / control should occupy its own line, as opposed to acting "inline" (multiple on same line) */
	block?: boolean;

	/** indicate if takes up the whole width of parent */
	fullWidth?: boolean;

	/** label (if not using localized resource) */
	label?: ReactNode;

	/** optional styles to add to the label */
	labelStyle?: CSSProperties;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** necessary to position full width labels appropriately */
	standalone?: boolean;

	/** indicate if wrap or not, true by default */
	wrap?: boolean;
};

const FormControlLabel: FunctionComponent<FormControlLabelProps> = (props) => {
	const { block, fullWidth, label, labelPlacement, labelStyle, res, resParams, standalone, wrap, sx, ...otherProps } = props;
	const [overflows, setOverflows] = useState(false);
	const text = res ? useLocale()
		.resource(res, resParams) : null;
	const finalText = text || label as ReactElement;
	const wrapValue = (wrap == undefined) ? true : wrap;
	let ref: (node: HTMLElement) => void;

	const controlBoxStyles: CSSProperties = {};
	const controlStyles: CSSProperties = {};
	let labelStyles = labelStyle ?? {};

	if (!wrapValue) {
		ref = useCallback((node: HTMLElement) => {
			if (node) {
				//based on the DOM structure, the second element would be the label
				const labelNode = node.childNodes[1] as HTMLElement;
				setOverflows(labelNode.scrollWidth > labelNode.clientWidth);
			}
		}, [label, text]);

		labelStyles.maxWidth = "100%";
		labelStyles = { ...labelStyles, ...NO_WRAP };
	}

	if (fullWidth) {
		controlBoxStyles.flexGrow = 1;
		controlBoxStyles.width = standalone ? "unset" : "100%";
		controlStyles.width = "100%";
	}

	const Label = (
		<MuiFormControlLabel
			label={finalText}
			labelPlacement={labelPlacement}
			ref={ref}
			sx={{
				display: block ? "flex" : undefined,
				maxWidth: wrap ? undefined : "100%",
				width: fullWidth ? "100%" : undefined,

				"& > div:first-of-type": controlBoxStyles,
				"& .MuiFormControlLabel-label": labelStyles,

				...sx,
			}}
			{...otherProps}
		/>
	);

	return wrapValue ? Label : (
		<Tooltip
			disableHoverListener={!overflows}
			title={finalText}
		>
			{Label}
		</Tooltip>
	);
};

export default FormControlLabel;